import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Form, Icon, Input } from "semantic-ui-react";

import { CUSTOM_CONFIRM_TYPES, CustomConfirm, useCan } from "components";
import { Element } from "components/ReactScroll/index";
import { createFormHelper } from "lib/Form";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import { ExactAccountModal, ExactSearch } from "modules/DealerDetails/components/Invoicing/components";
import {
  useDeleteFailedInvoiceMutation,
  useLinkExactAccountMutation,
  useRetrieveExactAccountMutation,
  useRetryExactInvoiceMutation
} from "modules/DealerDetails/components/Invoicing/hooks";

import type { Dealer } from "models";
import type { DealerAdminFormPayload } from "modules/DealerDetails";
import type { ExactAccount } from "modules/DealerDetails/components/Invoicing/hooks";

const FormHelper = createFormHelper<DealerAdminFormPayload>();

type InvoicingProps = {
  tabId: string;
  dealer: Dealer;
};

export const Invoicing = ({ tabId, dealer }: InvoicingProps) => {
  const { t } = useTranslation();
  const { watch } = FormHelper.useFormContext();

  const [account, setAccount] = useState<ExactAccount | null>(null);
  const [exactAccountId, setExactAccountId] = useState("");
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);
  const [isRetryConfirmOpen, setIsRetryConfirmOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const { mutate: linkExactAccount, isPending: isLinkExactAccountPending } = useLinkExactAccountMutation();
  const { mutate: retryExactInvoice, isPending: isRetryExactInvoicePending } = useRetryExactInvoiceMutation();
  const { mutate: deleteFailedInvoice, isPending: isDeleteFailedInvoicePending } = useDeleteFailedInvoiceMutation();
  const { mutate: retrieveExactAccount, isPending: isRetrieveExactAccountPending } = useRetrieveExactAccountMutation();

  const canRetryFailedInvoice = useCan(ACL_ACTIVITIES.Update, ACL_RESOURCES.ExactInvoice);
  const canDeleteFailedInvoice = useCan(ACL_ACTIVITIES.Delete, ACL_RESOURCES.ExactInvoice);

  const isExactInvoicingEnabled = watch("is_exact_invoicing_enabled");
  const areFailedInvoiceActionsDisabled = !dealer.has_failed_invoices || isRetryExactInvoicePending || isDeleteFailedInvoicePending;

  const handleLinkAccount = (accountId: string) => linkExactAccount({ dealer_id: dealer.id, account_id: accountId }, { onSuccess: () => setExactAccountId("") });

  const handleOpenAccountModal = () => {
    if (!dealer.exact_account_id) {
      setIsAccountModalOpen(true);
      return;
    }

    retrieveExactAccount(
      { dealer_id: dealer.id },
      {
        onSuccess: retrievedAccount => {
          setAccount(retrievedAccount);
          setIsAccountModalOpen(true);
        }
      }
    );
  };

  const handleCloseAccountModal = () => {
    setIsAccountModalOpen(false);
    setAccount(null);
  };

  return (
    <Element name={tabId} className="dealer-details-section">
      <div className="section-title">
        <div>
          <Icon className="file invoice dollar" />
        </div>
        <span className="section-title-text">{t("v8_invoice_settings", "Invoice Settings")}</span>
      </div>
      <div className="dealer-details-subheading">{t("v8_invoice_settings", "Invoice Settings")}</div>
      <Form.Group>
        <Form.Field width={16}>
          <FormHelper.Field.Checkbox name="is_exact_invoicing_enabled" label={t("v8_enable_dealer_level_invoicing", "Enable dealer level invoicing")} />
        </Form.Field>
      </Form.Group>

      {isExactInvoicingEnabled && (
        <>
          <Form.Group>
            <Form.Field width={8} required>
              <label>{t("v8_exact_account_id", "Exact Account ID")}</label>
              <Input
                value={dealer.exact_account_id ?? exactAccountId}
                readOnly={!!dealer.exact_account_id}
                placeholder={t("v8_exact_account_id", "Exact Account ID")}
                onChange={(_, { value }) => setExactAccountId(value)}
                action={
                  <>
                    <ExactSearch dealerId={dealer.id} onSelectAccount={handleLinkAccount} />
                    <Button
                      type="button"
                      color="green"
                      loading={isLinkExactAccountPending}
                      disabled={!!dealer.exact_account_id || isLinkExactAccountPending || !exactAccountId}
                      onClick={() => exactAccountId && handleLinkAccount(exactAccountId)}
                    >
                      {t("v8_link_account", "Link account")}
                    </Button>
                  </>
                }
              />
            </Form.Field>
          </Form.Group>
          <div className="dealer-details-button-row">
            <Button type="button" color="green" loading={isRetrieveExactAccountPending} disabled={isRetrieveExactAccountPending} onClick={handleOpenAccountModal}>
              {dealer.exact_account_id ? t("v8_update_exact_account", "Update Exact account") : t("v8_create_exact_account", "Create Exact account")}
            </Button>
          </div>

          {(canRetryFailedInvoice || canDeleteFailedInvoice) && (
            <>
              <div className="dealer-details-subheading">{t("v8_failed_invoices", "Failed invoices")}</div>
              <div className="dealer-details-button-row">
                {canRetryFailedInvoice && (
                  <Button type="button" color="orange" disabled={areFailedInvoiceActionsDisabled} onClick={() => setIsRetryConfirmOpen(true)}>
                    {t("v8_retry_sending_invoice", "Retry sending invoice")}
                    <Icon className="rotate right" />
                  </Button>
                )}
                {canDeleteFailedInvoice && (
                  <Button type="button" color="red" disabled={areFailedInvoiceActionsDisabled} onClick={() => setIsDeleteConfirmOpen(true)}>
                    {t("v8_delete_failed_invoice", "Delete failed invoice")}
                    <Icon className="trash" />
                  </Button>
                )}
              </div>
            </>
          )}
        </>
      )}
      <ExactAccountModal isOpen={isAccountModalOpen} account={account} dealerId={dealer.id} onClose={handleCloseAccountModal} onSaved={() => setExactAccountId("")} />
      <CustomConfirm
        type={CUSTOM_CONFIRM_TYPES.Warning}
        isOpen={isRetryConfirmOpen}
        isLoading={isRetryExactInvoicePending}
        confirmMsg={t("v8_confirm_retry_failed_invoice", "Are you sure that you want to retry this invoice?")}
        handleCancel={() => setIsRetryConfirmOpen(false)}
        handleConfirm={() => retryExactInvoice({ dealer_id: dealer.id }, { onSuccess: () => setIsRetryConfirmOpen(false) })}
      />
      <CustomConfirm
        type={CUSTOM_CONFIRM_TYPES.Danger}
        isOpen={isDeleteConfirmOpen}
        isLoading={isDeleteFailedInvoicePending}
        confirmMsg={t("v8_confirm_delete_failed_invoice", "Are you sure that you want to delete this invoice?")}
        handleCancel={() => setIsDeleteConfirmOpen(false)}
        handleConfirm={() => deleteFailedInvoice({ dealer_id: dealer.id }, { onSuccess: () => setIsDeleteConfirmOpen(false) })}
      />
    </Element>
  );
};
